import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { escHtml } from '../utils/escapeHtml';

export interface ClusterPoint {
  id: string;
  lat: number;
  lng: number;
  color: string;
  radius?: number;
  strokeColor?: string;
  weight?: number;
  tooltip?: string;            // texto do tooltip (ex.: número da parada ou nome do vendedor)
  tooltipPermanent?: boolean;  // default true; false = só no hover
  popupHtml: string;           // conteúdo do popup; botões com data-cluster-action="<id>" disparam onAction
  noCluster?: boolean;         // true = sempre visível no mapa (fora do cluster), ex.: paradas da rota
}

interface Props {
  points: ClusterPoint[];
  onAction?: (id: string) => void;
}

// Agrupa muitos marcadores (markercluster) de forma imperativa — leve mesmo com centenas de pontos.
export default function LeafletCluster({ points, onAction }: Props) {
  const map = useMap();
  const actionRef = useRef(onAction);
  actionRef.current = onAction;

  useEffect(() => {
    const cluster = (L as any).markerClusterGroup({
      // NÃO reative o chunkedLoading: ele adiciona os marcadores em lotes assíncronos (setTimeout);
      // se o efeito for desmontado/recriado (StrictMode, ou `points` mudando) antes de um lote rodar,
      // o cleanup zera o `_map` e o lote pendente estoura em `_map.getMinZoom()` (null). Síncrono é seguro.
      chunkedLoading: false,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,          // marcadores sobrepostos se abrem em leque ao clicar
      spiderfyDistanceMultiplier: 1.8,  // abre mais espaçado p/ dar pra ler/clicar
      showCoverageOnHover: false,       // remove o polígono azul (poluição visual)
    });
    const direct: L.Layer[] = [];

    points.forEach((p) => {
      const m = L.circleMarker([p.lat, p.lng], {
        radius: p.radius ?? 7,
        color: p.strokeColor ?? '#fff', // anel branco separa bolinhas sobrepostas
        weight: p.weight ?? 2,
        fillColor: p.color,
        fillOpacity: 1,                 // preenchimento sólido melhora leitura quando empilha
      });
      if (p.tooltip) {
        const permanent = p.tooltipPermanent !== false;
        // ESCAPADO aqui no centro: `tooltip` é texto puro vindo de dado do ERP (nome de cliente/vendedor)
        // e o Leaflet insere como HTML. `popupHtml` NÃO passa por aqui — quem monta já escapa os campos.
        m.bindTooltip(escHtml(p.tooltip), { permanent, direction: permanent ? 'center' : 'top' });
      }
      m.bindPopup(p.popupHtml);
      if (p.noCluster) { m.addTo(map); direct.push(m); } else { cluster.addLayer(m); }
    });
    map.addLayer(cluster);

    const clickHandler = (ev: MouseEvent) => {
      const t = ev.target as HTMLElement;
      const id = t?.getAttribute?.('data-cluster-action');
      if (id && actionRef.current) actionRef.current(id);
    };
    document.addEventListener('click', clickHandler);

    return () => {
      document.removeEventListener('click', clickHandler);
      map.removeLayer(cluster);
      direct.forEach((l) => map.removeLayer(l));
    };
  }, [points, map]);

  return null;
}
